import { z } from 'zod';
import { PaginationParamsSchema, PaginationResponseSchema } from '../common';

/** Half a star is the lowest a viewer can give — 0 means "no rating yet". */
export const EVENT_FEEDBACK_RATING_MIN = 0.5;
export const EVENT_FEEDBACK_RATING_MAX = 5;
export const EVENT_FEEDBACK_RATING_STEP = 0.5;
export const EVENT_FEEDBACK_MESSAGE_MIN = 10;
export const EVENT_FEEDBACK_MESSAGE_MAX = 2000;

const RATING_RANGE_ERROR = `Please choose a rating between ${EVENT_FEEDBACK_RATING_MIN} and ${EVENT_FEEDBACK_RATING_MAX} stars.`;

const EventFeedbackRatingSchema = z
  .number({ error: 'Please select a rating.' })
  .min(EVENT_FEEDBACK_RATING_MIN, RATING_RANGE_ERROR)
  .max(EVENT_FEEDBACK_RATING_MAX, RATING_RANGE_ERROR)
  .multipleOf(EVENT_FEEDBACK_RATING_STEP, 'Please rate in half stars, like 3 or 3.5.');

/**
 * `POST /viewer/events/:id/feedback`. Feedback is append-only — each submission
 * is a new record, so a viewer may submit more than once per event.
 */
export const CreateEventFeedbackRequestSchema = z.strictObject(
  {
    rating: EventFeedbackRatingSchema,
    message: z
      .string({ error: 'Please write your feedback.' })
      .trim()
      .min(
        EVENT_FEEDBACK_MESSAGE_MIN,
        `Please write at least ${EVENT_FEEDBACK_MESSAGE_MIN} characters of feedback.`,
      )
      .max(
        EVENT_FEEDBACK_MESSAGE_MAX,
        `Please keep your feedback under ${EVENT_FEEDBACK_MESSAGE_MAX} characters.`,
      ),
  },
  // `.strict({ error })` is ignored in Zod v4; only this form applies.
  { error: 'Please send only a rating and a message.' },
);

export const EventFeedbackResponseSchema = z.object({
  id: z.uuid(),
  eventId: z.uuid(),
  viewerId: z.uuid(),
  rating: EventFeedbackRatingSchema,
  message: z.string(),
  createdAt: z.iso.datetime(),
});

export const HostEventFeedbackViewerSchema = z.object({
  id: z.uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});

export const HostEventFeedbackItemSchema = z.object({
  id: z.uuid(),
  rating: EventFeedbackRatingSchema,
  message: z.string(),
  viewer: HostEventFeedbackViewerSchema,
  createdAt: z.iso.datetime(),
});

/**
 * `averageRating` counts each viewer's most recent submission only, rounded to
 * one decimal place; 0 when the event has no feedback.
 */
export const HostEventFeedbackSummarySchema = z.object({
  averageRating: z.number().min(0).max(EVENT_FEEDBACK_RATING_MAX),
});

export const ListHostEventFeedbackQuerySchema = PaginationParamsSchema;

export const PagedHostEventFeedbackResponseSchema = z.object({
  items: z.array(HostEventFeedbackItemSchema),
  summary: HostEventFeedbackSummarySchema,
  ...PaginationResponseSchema.shape,
});
